/* White House Food: Build Your Box */
(() => {
  const root = document.querySelector('[data-whf-builder]');
  if (!root) return;

  const clean = (list) => (list || []).map((s) => String(s).trim()).filter(Boolean);
  const raw = JSON.parse(root.querySelector('[data-whf-config]').textContent);
  const app = root.querySelector('[data-whf-app]');
  const policyHtml = root.querySelector('[data-whf-policy]').innerHTML;

  const C = {
    ...raw,
    blackouts: clean(raw.blackouts),
    chocolates: clean(raw.chocolates),
    coatings: clean(raw.coatings),
    candies: clean(raw.candies),
    colors: clean(raw.colors).map((l) => {
      const [name, hex] = l.split('|').map((s) => s.trim());
      return { name, hex: hex || '#ccc' };
    }),
  };

  const month = new Date().getMonth() + 1;
  const seasonal = clean(raw.seasonalCakePops)
    .map((l) => {
      const [name, range] = l.split('|').map((s) => s.trim());
      const [a, b] = (range || '1-12').split('-').map(Number);
      return { name, a, b: b || a };
    })
    .filter((s) => (s.a <= s.b ? month >= s.a && month <= s.b : month >= s.a || month <= s.b))
    .map((s) => s.name);
  C.cakePopFlavors = [...clean(raw.cakePopFlavors), ...seasonal];
  C.cookies = (raw.cookies || []).filter((c) => c.available);

  /* ---------- helpers ---------- */
  const money = (cents) => {
    const v = (cents / 100).toFixed(2).replace(/\.00$/, '');
    return '$' + v.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };
  const esc = (s) =>
    String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const variant = (list, title) => list.find((v) => v.title.toLowerCase() === title.toLowerCase());
  const sum = (obj) => Object.values(obj).reduce((a, b) => a + b, 0);
  const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const parseISO = (s) => {
    const [y, m, d] = s.split('-').map(Number);
    return new Date(y, m - 1, d);
  };
  const prettyDate = (s) =>
    parseISO(s).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

  const DIPPED = [
    { key: 'oreos', label: 'Dipped Oreos', extra: 'Dipped Oreos' },
    { key: 'pretzels', label: 'Dipped Pretzels', extra: 'Dipped Pretzels' },
    { key: 'rkt', label: 'Rice Krispie Treats', extra: 'Rice Krispie Treats' },
  ];
  const IMAGE_ELIGIBLE = ['oreos', 'rkt', 'cakePops'];

  /* Oreo types: "Name | flavored barks it pairs with | almond bark it is always dipped in" */
  const keyOf = (s) => String(s).toLowerCase().replace(/\(.*?\)/g, '').replace(/\s+/g, ' ').trim();
  const plainName = (s) => String(s).replace(/\s*\(.*?\)/g, '').trim();
  const joinWith = (list, word) =>
    list.length < 3 ? list.join(` ${word} `) : `${list.slice(0, -1).join(', ')}, ${word} ${list[list.length - 1]}`;
  const OT = clean(raw.oreoTypes)
    .map((l) => {
      const [nameRaw, flavors, base] = l.split('|').map((s) => s.trim());
      const name = (nameRaw || '').replace(/\s*oreos?$/i, '').trim();
      const keys = !flavors || /^all$/i.test(flavors) ? null : flavors.split(',').map(keyOf).filter(Boolean);
      return {
        name,
        label: `${name} Oreos`,
        flavors: keys ? C.coatings.filter((c) => keys.includes(keyOf(c))) : C.coatings.slice(),
        base: base ? C.chocolates.find((c) => keyOf(c) === keyOf(base)) || base : '',
      };
    })
    .filter((t) => t.name);
  // one friendly line explaining what a type can't have
  const pairNote = (ot) => {
    const out = C.coatings.filter((c) => !ot.flavors.includes(c));
    const baseBit = ot.base ? `${ot.label} are dipped in ${ot.base.toLowerCase()}` : '';
    if (!out.length) return baseBit ? `${baseBit}.` : '';
    if (!ot.flavors.length) return baseBit ? `${baseBit} with no flavored bark.` : `${ot.label} come in plain almond bark only.`;
    if (out.length > ot.flavors.length || baseBit)
      return `${baseBit ? `${baseBit} and pair` : `${ot.label} pair`} with ${joinWith(ot.flavors.map(plainName), 'or')} bark only.`;
    return out
      .map((c) => {
        const fits = OT.filter((t) => t.flavors.includes(c)).map((t) => t.label);
        return fits.length ? `${plainName(c)} bark is made for ${joinWith(fits, 'and')}.` : `${plainName(c)} bark isn't available on Oreos.`;
      })
      .join(' ');
  };

  const DAY_NAMES = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  const closedWeekdays = new Set(
    String(C.closedDays || '')
      .split(/[,;\n]+/)
      .map((d) => DAY_NAMES.indexOf(d.trim().toLowerCase().slice(0, 3)))
      .filter((n) => n >= 0)
  );
  const blackoutSet = new Set();
  C.blackouts.forEach((line) => {
    const [a, b] = line.split(/\s+to\s+/i).map((s) => s.trim());
    if (!/^\d{4}-\d{2}-\d{2}$/.test(a)) return;
    const end = b && /^\d{4}-\d{2}-\d{2}$/.test(b) ? parseISO(b) : parseISO(a);
    for (let d = parseISO(a); d <= end; d.setDate(d.getDate() + 1)) blackoutSet.add(iso(d));
  });

  /* ---------- state ---------- */
  const STEPS = ['Size', 'Treats', 'Make it yours', 'Review'];
  const params = new URLSearchParams(location.search);
  const S = {
    step: 0,
    pkg: C.packages.find((p) => p.handle === params.get('package')) || null,
    date: null,
    calMonth: null,
    mix: { oreos: 0, pretzels: 0, rkt: 0 },
    extra: { oreos: 0, pretzels: 0, rkt: 0, caramel: 0 },
    bigfoot: { Chocolate: 0, White: 0 }, // Bigfoot Bites add-on, by 3s
    cakePops: {}, // flavor -> count
    cakePopShape: 'Round', // Round (default) or Cakesicle
    cookies: {}, // variant id -> included count
    cookieExtra: {}, // variant id -> extra count
    coatings: { oreos: {}, pretzels: {}, rkt: {} }, // type -> coating -> count
    oreoType: OT.map(() => 0), // Oreo type index -> count
    oreoCoat: OT.map(() => ({})), // Oreo type index -> coating -> count (rolls up into coatings.oreos)
    oreoSeen: 0, // Oreo total last time we balanced types
    caramelUpgrade: 0,
    candyCount: { oreos: 0, pretzels: 0, rkt: 0 },
    candyChoices: [],
    candyOther: '',
    showErrors: false,
    bases: (() => { const v = C.chocolates.find((c) => /vanilla|white/i.test(c)); return v ? [v] : C.chocolates.slice(0, 1); })(),
    baseNotice: false,
    colors: [],
    customColor: '',
    decor: 'simple',
    bags: false,
    ribbon: false,
    occasion: '',
    personalization: '',
    images: 0,
    photos: 1,
    customImages: 0,
    notes: '',
    files: [],
    edibleFiles: [],
    agreed: false,
    payment: 'deposit',
    error: '',
    busy: false,
  };
  if (S.pkg) S.step = 1;

  /* ---------- derived counts ---------- */
  const incl = (key) => (!S.pkg ? 0 : S.pkg.mixDipped > 0 ? S.mix[key] : S.pkg[key]);
  const dippedTotal = (key) => incl(key) + S.extra[key];
  const cakePopIncluded = () => (S.pkg ? S.pkg.cakePops : 0);
  const cakePopTotal = () => sum(S.cakePops);
  const cookieIncluded = () => (S.pkg ? S.pkg.cookies : 0);
  const stickTreats = () => DIPPED.reduce((a, t) => a + dippedTotal(t.key), 0) + S.extra.caramel + cakePopTotal();
  const chocolateBase = () => C.chocolates.find((c) => /chocolate/i.test(c));
  const blackChosen = () => S.colors.some((c) => /black/i.test(c)) || /\bblack\b/i.test(S.customColor);
  // edible images sit on white Bigfoot Bite tops too
  const imageEligible = () => dippedTotal('oreos') + dippedTotal('rkt') + cakePopTotal() + S.bigfoot.White;
  const BIGFOOT = ['Chocolate', 'White'];
  const bigfootTotal = () => sum(S.bigfoot);

  /* ---------- pricing ---------- */
  const up = (title) => variant(C.upgrades, title);
  function lines() {
    const out = []; // {label, cents, cart:[{id, quantity, sellingPlan}]}
    if (!S.pkg) return out;
    out.push({ label: S.pkg.name, cents: S.pkg.price, main: true });

    const addDozens = (label, base, n) => {
      if (n <= 0) return;
      const dz = variant(C.extras, `${base} (Dozen)`);
      const hf = variant(C.extras, `${base} (Half Dozen)`);
      const dozens = Math.floor(n / 12);
      const halves = Math.round((n % 12) / 6);
      const cart = [];
      let cents = 0;
      if (dozens && dz) (cents += dz.price * dozens), cart.push({ v: dz, q: dozens });
      if (halves && hf) (cents += hf.price * halves), cart.push({ v: hf, q: halves });
      out.push({ label: `Extra ${label} (${n})`, cents, cart });
    };
    DIPPED.forEach((t) => addDozens(t.label, t.extra, S.extra[t.key]));
    addDozens('Caramel-Wrapped Pretzels', 'Caramel-Wrapped Pretzels', S.extra.caramel);
    const cpExtra = Math.max(0, cakePopTotal() - cakePopIncluded());
    addDozens('Cake Pops', 'Cake Pops', cpExtra);

    // Bigfoot Bites: 6-packs first, then a 3-pack
    BIGFOOT.forEach((coat) => {
      const n = S.bigfoot[coat];
      if (n <= 0) return;
      const six = variant(C.extras, `Bigfoot Bites, ${coat} (6-Pack)`);
      const three = variant(C.extras, `Bigfoot Bites, ${coat} (3-Pack)`);
      const sixes = Math.floor(n / 6);
      const threes = Math.round((n % 6) / 3);
      const cart = [];
      let cents = 0;
      if (sixes && six) (cents += six.price * sixes), cart.push({ v: six, q: sixes });
      if (threes && three) (cents += three.price * threes), cart.push({ v: three, q: threes });
      out.push({ label: `Bigfoot Bites, ${coat.toLowerCase()} (${n})`, cents, cart });
    });

    C.cookies.forEach((c) => {
      const n = S.cookieExtra[c.id] || 0;
      if (n > 0) out.push({ label: `Extra ${c.title} cookies (${n})`, cents: c.price * (n / 6), cart: [{ v: c, q: n / 6 }] });
    });

    const perHalf = (title, n, label) => {
      const v = up(title);
      if (!v || n <= 0) return;
      const q = Math.ceil(n / 6);
      out.push({ label, cents: v.price * q, cart: [{ v, q }] });
    };
    const coated = DIPPED.reduce((a, t) => a + sum(S.coatings[t.key]), 0);
    perHalf('Flavored Bark (per half dozen)', coated, `Flavored bark (${coated})`);
    perHalf('Caramel Wrap Upgrade (per half dozen)', S.caramelUpgrade, `Caramel-wrap upgrade (${S.caramelUpgrade})`);
    const candy = sum(S.candyCount);
    perHalf('Candy Topping (per half dozen)', candy, `Candy toppings (${candy})`);
    if (S.decor === 'detailed') perHalf('Detailed Decorating (per half dozen)', stickTreats(), 'Detailed decorating');
    if (S.decor === 'showstopper') perHalf('Showstopper Decorating (per half dozen)', stickTreats(), 'Showstopper decorating');
    if (S.bags) perHalf('Individual Bag (per half dozen)', stickTreats(), 'Individual bags');
    if (S.ribbon) perHalf('Ribbon Tie (per half dozen)', stickTreats(), 'Ribbon ties');

    const colorCount = S.colors.length + (S.customColor.trim() ? 1 : 0);
    const extraColors = Math.max(0, colorCount - C.freeColors);
    const ec = up('Extra Color (over 4)');
    if (extraColors && ec) out.push({ label: `Extra colors (${extraColors})`, cents: ec.price * extraColors, cart: [{ v: ec, q: extraColors }] });

    if (S.images > 0) {
      const sheets = Math.ceil(S.images / 20);
      const first = up('Edible Images (First Sheet)');
      const more = up('Edible Images (Additional Sheet)');
      const cart = [];
      let cents = 0;
      if (first) (cents += first.price), cart.push({ v: first, q: 1 });
      if (sheets > 1 && more) (cents += more.price * (sheets - 1)), cart.push({ v: more, q: sheets - 1 });
      out.push({ label: `Edible images (${S.images} treats, ${sheets} sheet${sheets > 1 ? 's' : ''})`, cents, cart });
      const extraPhotos = Math.max(0, S.edibleFiles.length + S.customImages - C.maxPhotos * sheets);
      const ep = up('Extra Edible Photo');
      if (extraPhotos && ep) out.push({ label: `Extra photos (${extraPhotos})`, cents: ep.price * extraPhotos, cart: [{ v: ep, q: extraPhotos }] });
      const ci = up('Custom Designed Image');
      if (S.customImages && ci) out.push({ label: `Custom-designed images (${S.customImages})`, cents: ci.price * S.customImages, cart: [{ v: ci, q: S.customImages }] });
    }
    return out;
  }
  const total = () => lines().reduce((a, l) => a + l.cents, 0);

  /* ---------- validation ---------- */
  function problems(step = S.step) {
    const p = [];
    if (step >= 0 && !S.pkg) p.push('Choose a box size.');
    if (step >= 1 && S.pkg) {
      if (S.pkg.mixDipped > 0 && sum(S.mix) !== S.pkg.mixDipped)
        p.push(`Choose ${S.pkg.mixDipped - sum(S.mix)} more dipped treats.`);
      if (cakePopIncluded() > 0 && cakePopTotal() < cakePopIncluded())
        p.push(`Pick a flavor for your ${cakePopIncluded()} cake pops.`);
      const ck = sum(S.cookies);
      if (cookieIncluded() > 0 && ck !== cookieIncluded()) p.push(`Choose ${cookieIncluded() - ck} more cookies.`);
    }
    if (step >= 2 && !S.bases.length) p.push('Choose your almond bark base: chocolate, vanilla, or both.');
    if (step >= 2) p.push(...oreoProblems());
    if (step >= 2 && sum(S.candyCount) > 0 && !S.candyChoices.length && !S.candyOther.trim())
      p.push('Choose which candy you would like, or type your own.');
    if (step >= 2 && S.images > 0 && !S.customImages && !S.edibleFiles.length)
      p.push('Upload the photo(s) for your edible toppers, or add a custom-designed image and I will make it for you.');
    // when Joni designs it she still needs direction: an invitation, a palette, or a note
    if (step >= 2 && S.customImages > 0 && !S.files.length && !S.occasion.trim() && !S.personalization.trim() && !S.notes.trim())
      p.push('Tell me what to design: add an inspiration photo like the invitation, or describe it in the occasion or notes.');
    if (step >= 3 && !S.agreed) p.push('Please check the box to agree to the order policy.');
    return p;
  }

  function oreoProblems() {
    const p = [];
    const n = dippedTotal('oreos');
    if (!OT.length || !n) return p;
    const chosen = sum(S.oreoType);
    if (chosen < n) p.push(`Choose an Oreo type for ${n - chosen} more Oreos (${chosen} of ${n} chosen).`);
    if (chosen > n) p.push(`Your Oreo types add up to ${chosen}, but your box has ${n} Oreos. Please take off ${chosen - n}.`);
    OT.forEach((ot, i) => {
      const count = S.oreoType[i];
      Object.entries(S.oreoCoat[i]).forEach(([c, v]) => {
        if (!v || ot.flavors.includes(c)) return;
        const fits = OT.filter((t) => t.flavors.includes(c)).map((t) => t.label);
        const why = fits.length
          ? `${plainName(c)} bark is made for ${joinWith(fits, 'and')}`
          : `${plainName(c)} bark isn't available on Oreos`;
        p.push(`${why}. Please change the ${v} ${plainName(c)} ${ot.label} to another flavor${fits.length ? ` or switch them to ${joinWith(fits, 'or')}` : ''}.`);
      });
      const coated = sum(S.oreoCoat[i]);
      if (coated > count)
        p.push(`You chose flavored bark for ${coated} ${ot.label}, but you only have ${count}. Please take off ${coated - count}.`);
    });
    return p;
  }

  /* ---------- capped stepper groups ---------- */
  // When every treat in a group is already assigned, tapping + moves the half dozen
  // over from the sibling with the most, so one tap swaps instead of doing nothing.
  const CARAMEL = 'caramel'; // caramel wrap shares the pretzel allotment
  const groupUsed = (read, keys) => keys.reduce((a, k) => a + read(k), 0);
  const biggestSibling = (read, keys, key) => {
    let from = null;
    keys.forEach((k) => {
      if (k === key || read(k) <= 0) return;
      if (from === null || read(k) > read(from)) from = k;
    });
    return from;
  };
  const hasRoom = (read, keys, key, cap, step = 6) =>
    cap - groupUsed(read, keys) >= step || biggestSibling(read, keys, key) !== null;
  function moveInGroup(read, write, keys, key, delta, cap, hasPool = true) {
    if (delta > 0) {
      let need = delta - Math.max(0, cap - groupUsed(read, keys));
      while (need > 0) {
        const from = biggestSibling(read, keys, key);
        if (from === null) break;
        const take = Math.min(need, read(from));
        write(from, read(from) - take);
        need -= take;
      }
      write(key, read(key) + delta - Math.max(0, need));
      return;
    }
    const give = Math.min(-delta, read(key));
    if (!give) return;
    if (hasPool) return write(key, read(key) - give); // the freed treats go back to plain
    const sib = biggestSibling(read, keys, key);
    const to = sib !== null ? sib : keys[0] === key ? null : keys[0];
    if (to === null) return; // nowhere to put them, so nothing to give up
    write(key, read(key) - give);
    write(to, read(to) + give);
  }
  const mixKeys = DIPPED.map((t) => t.key);
  const mixRead = (k) => S.mix[k];
  const mixWrite = (k, v) => (S.mix[k] = Math.max(0, v));
  const cookieKeys = () => C.cookies.map((c) => String(c.id));
  const cookieRead = (k) => S.cookies[k] || 0;
  const cookieWrite = (k, v) => (S.cookies[k] = Math.max(0, v));
  const coatKeys = (t) => {
    const keys = [...new Set([...C.coatings, ...Object.keys(S.coatings[t])])];
    return t === 'pretzels' ? [...keys, CARAMEL] : keys;
  };
  const coatRead = (t) => (k) => (k === CARAMEL ? S.caramelUpgrade : S.coatings[t][k] || 0);
  const coatWrite = (t) => (k, v) => {
    if (k === CARAMEL) S.caramelUpgrade = Math.max(0, v);
    else S.coatings[t][k] = Math.max(0, v);
  };
  const oreoCoatKeys = (i) => [...new Set([...OT[i].flavors, ...Object.keys(S.oreoCoat[i])])];
  const oreoCoatRead = (i) => (k) => S.oreoCoat[i][k] || 0;
  const oreoCoatWrite = (i) => (k, v) => (S.oreoCoat[i][k] = Math.max(0, v));
  const typeKeys = () => OT.map((_, i) => i);
  const typeRead = (k) => S.oreoType[k];
  const typeWrite = (k, v) => (S.oreoType[k] = Math.max(0, v));

  /* ---------- rendering ---------- */
  const stepper =(name, value, { step = 6, min = 0, max = 999, label = '', sub = '' } = {}) => `
    <div class="whf-stepper">
      <div class="whf-stepper__text"><span class="whf-stepper__label">${label}</span>${sub ? `<span class="whf-stepper__sub">${sub}</span>` : ''}</div>
      <div class="whf-stepper__controls">
        <button type="button" data-step="${name}" data-delta="${-step}" ${value - step < min ? 'disabled' : ''} aria-label="Less ${esc(label)}">−</button>
        <output>${value}</output>
        <button type="button" data-step="${name}" data-delta="${step}" ${value + step > max ? 'disabled' : ''} aria-label="More ${esc(label)}">+</button>
      </div>
    </div>`;

  function gallery(p, extra = '') {
    const imgs = p.images && p.images.length ? p.images : p.image ? [p.image] : [];
    if (!imgs.length) return '';
    const nav = imgs.length > 1
      ? `<button type="button" class="whf-gal__nav whf-gal__nav--prev" data-galnav="-1" aria-label="Previous photo">&#8249;</button><button type="button" class="whf-gal__nav whf-gal__nav--next" data-galnav="1" aria-label="Next photo">&#8250;</button><span class="whf-gal__count">1 / ${imgs.length}</span>`
      : '';
    return `<div class="whf-gal ${extra}"><div class="whf-gal__track">${imgs.map((u, i) => `<img src="${u}" alt="${esc(p.name)}, photo ${i + 1}" loading="lazy">`).join('')}</div>${nav}</div>`;
  }

  function viewSize() {
    return `
      <h2 class="whf-h2">Choose your size</h2>
      <p class="whf-help">Every box can be customized with flavors, colors, and add-ons in the next steps.</p>
      ${C.seasonals && C.seasonals.length ? `<div class="whf-seasonals">${C.seasonals.map((s) => `<a class="whf-seasonal" href="${s.url}">${s.image ? `<img src="${s.image}" alt="" loading="lazy">` : ''}<span class="whf-seasonal__body"><span class="whf-seasonal__label">Limited seasonal box</span><strong>${esc(s.title)}</strong><span>${esc(C.seasonalNote || '')}</span><span class="whf-seasonal__cta">${money(s.price)} · See the box &#8250;</span></span></a>`).join('')}</div>` : ''}
      <div class="whf-packages">
        ${C.packages
          .map(
            (p) => {
              const sel = S.pkg && S.pkg.handle === p.handle;
              const gal = gallery(p);
              return `<div class="whf-pkg-wrap ${sel ? 'is-selected' : ''}">${gal}
          <button type="button" class="whf-pkg ${sel ? 'is-selected' : ''}" data-pkg="${p.handle}">
            <span class="whf-pkg__body">
              <span class="whf-pkg__name">${esc(p.name)}</span>
              <span class="whf-pkg__tag">${esc(p.tagline)}</span>
              <span class="whf-pkg__meta">${contents(p)}</span>
              <span class="whf-pkg__foot"><strong>${money(p.price)}</strong><span>Serves ${esc(p.serves)}</span></span>
            </span>
          </button></div>`;
            }
          )
          .join('')}
      </div>`;
  }
  function contents(p) {
    const bits = [];
    if (p.mixDipped) bits.push(`${p.mixDipped} dipped treats, mix &amp; match`);
    if (p.oreos) bits.push(`${p.oreos} Oreos`);
    if (p.pretzels) bits.push(`${p.pretzels} pretzels`);
    if (p.rkt) bits.push(`${p.rkt} Rice Krispies`);
    if (p.cakePops) bits.push(`${p.cakePops} cake pops`);
    if (p.cookies) bits.push(`${p.cookies} cookies`);
    return bits.join(' · ');
  }

  function dayStatus(d) {
    const key = iso(d);
    const earliest = new Date();
    earliest.setHours(0, 0, 0, 0);
    earliest.setDate(earliest.getDate() + C.leadDays);
    if (d < earliest) return 'soon';
    if (blackoutSet.has(key)) return 'closed';
    if (closedWeekdays.has(d.getDay())) return 'closed';
    const used = Number(C.booked[key] || 0);
    if (used + (S.pkg ? S.pkg.points : 1) > C.dailyCapacity) return 'full';
    return 'open';
  }
  function viewDate() {
    const first = new Date();
    first.setHours(0, 0, 0, 0);
    first.setDate(first.getDate() + C.leadDays);
    if (!S.calMonth) S.calMonth = S.date ? parseISO(S.date) : new Date(first);
    const m = new Date(S.calMonth.getFullYear(), S.calMonth.getMonth(), 1);
    const minMonth = new Date(first.getFullYear(), first.getMonth(), 1);
    const cells = [];
    for (let i = 0; i < m.getDay(); i++) cells.push('<span></span>');
    const days = new Date(m.getFullYear(), m.getMonth() + 1, 0).getDate();
    for (let n = 1; n <= days; n++) {
      const d = new Date(m.getFullYear(), m.getMonth(), n);
      const st = dayStatus(d);
      const key = iso(d);
      const titles = { soon: 'Needs more notice', closed: 'Closed', full: 'Fully booked', open: 'Available' };
      cells.push(
        `<button type="button" class="whf-day is-${st} ${S.date === key ? 'is-selected' : ''}" data-date="${key}" ${st !== 'open' ? 'disabled' : ''} aria-label="${esc(prettyDate(key))}, ${titles[st]}">${n}</button>`
      );
    }
    return `
      <h2 class="whf-h2">Pick your pickup date</h2>
      <p class="whf-help">We need at least ${Math.round(C.leadDays / 7)} weeks' notice to make it perfect. Need it sooner? <a href="/pages/contact">Ask about a rush order</a>.</p>
      <p class="whf-help whf-help--small">Pickups are Wednesday through Sunday. Weekday pickups are after 5:30 pm, and weekends are flexible. Need a lunchtime pickup? Add it in special requests and we will try to make it work.</p>
      <div class="whf-cal">
        <div class="whf-cal__head">
          <button type="button" data-month="-1" ${m <= minMonth ? 'disabled' : ''} aria-label="Previous month">‹</button>
          <strong>${m.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</strong>
          <button type="button" data-month="1" aria-label="Next month">›</button>
        </div>
        <div class="whf-cal__dow">${['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d) => `<span>${d}</span>`).join('')}</div>
        <div class="whf-cal__grid">${cells.join('')}</div>
        <div class="whf-cal__legend"><span class="k open"></span>Available <span class="k full"></span>Booked / closed</div>
      </div>
      ${S.date ? `<p class="whf-picked">Pickup: <strong>${prettyDate(S.date)}</strong> · Mountain Home, AR. We'll confirm your pickup time.</p>` : ''}`;
  }

  function viewTreats() {
    const p = S.pkg;
    let html = `<h2 class="whf-h2">Choose your treats</h2>
      <p class="whf-help whf-lede">I already filled your box with a good mix. Change anything you like, or leave it as is.</p>
      <section class="whf-card whf-yourbox">${gallery(p, 'whf-gal--short')}
        <div class="whf-yourbox__row"><div class="whf-yourbox__text"><span class="whf-yourbox__label">Your box</span><strong>${esc(p.name)}</strong><span>${contents(p)} · Serves ${esc(p.serves)}</span></div>
        <button type="button" class="whf-yourbox__change" data-goto="0">Change size</button></div></section>`;

    // Dipped
    html += `<section class="whf-card"><h3 class="whf-h3">Dipped treats</h3>`;
    if (p.mixDipped > 0) {
      const left = p.mixDipped - sum(S.mix);
      html += `<p class="whf-help">${p.mixDipped} dipped treats, adjust any of them in half dozens. <span class="whf-count ${left === 0 ? 'is-done' : ''}">${sum(S.mix)} of ${p.mixDipped}</span></p>
        <button type="button" class="whf-pickforme" data-pick="mix">${sum(S.mix) === S.pkg.mixDipped ? "Reset to Joni's mix" : "Let Joni pick my mix"}</button>`;
      DIPPED.forEach((t) => {
        const v = S.mix[t.key];
        html += stepper(`mix.${t.key}`, v, {
          label: t.label,
          sub: 'included',
          max: hasRoom(mixRead, mixKeys, t.key, p.mixDipped) ? v + 6 : v,
        });
      });
    } else {
      const inc = DIPPED.filter((t) => p[t.key]).map((t) => `${p[t.key]} ${t.label}`);
      if (inc.length) html += `<p class="whf-help">Included: ${inc.join(' · ')}</p>`;
    }
    const extrasChosen = sum(S.extra);
    html += `<details class="whf-more"${extrasChosen ? ' open' : ''}><summary>Add more dipped treats${extrasChosen ? ` <span class="whf-count is-done">${extrasChosen} added</span>` : ''}</summary><div class="whf-more__body"><p class="whf-help">By the half dozen, on top of what is included.</p>`;
    DIPPED.forEach((t) => {
      const dz = variant(C.extras, `${t.extra} (Dozen)`);
      const hf = variant(C.extras, `${t.extra} (Half Dozen)`);
      html += stepper(`extra.${t.key}`, S.extra[t.key], { label: t.label, sub: hf && dz ? `${money(hf.price)} / half dozen · ${money(dz.price)} / dozen` : '' });
    });
    const cz = variant(C.extras, 'Caramel-Wrapped Pretzels (Dozen)');
    const ch = variant(C.extras, 'Caramel-Wrapped Pretzels (Half Dozen)');
    html += stepper('extra.caramel', S.extra.caramel, { label: 'Caramel-Wrapped Pretzels', sub: cz && ch ? `${money(ch.price)} / half dozen · ${money(cz.price)} / dozen` : '' });
    html += `</div></details></section>`;

    // Cake pops
    const cpInc = cakePopIncluded();
    const cpd = variant(C.extras, 'Cake Pops (Dozen)');
    const cph = variant(C.extras, 'Cake Pops (Half Dozen)');
    const cpOpen = cpInc > 0 || cakePopTotal() > 0;
    html += `<section class="whf-card"><details class="whf-more"${cpOpen ? ' open' : ''}><summary><span class="whf-h3">Cake pops</span>${cpInc ? ` <span class="whf-count ${cakePopTotal() >= cpInc ? 'is-done' : ''}">${Math.min(cakePopTotal(), cpInc)} of ${cpInc}</span>` : cakePopTotal() ? ` <span class="whf-count is-done">${cakePopTotal()} added</span>` : ` <span class="whf-more__hint">optional</span>`}</summary><div class="whf-more__body">
      <p class="whf-help">${cpInc ? `${cpInc} included. ` : ''}Each new flavor starts at a dozen; add more of a flavor by the half dozen.${cpd && cph ? ` Extras ${money(cph.price)} / half dozen · ${money(cpd.price)} / dozen.` : ''}</p>
      ${cpInc ? `<button type="button" class="whf-pickforme" data-pick="cakepops">"Reset to Joni's flavors"</button>` : ''}`;
    html += `<p class="whf-help">Shape: round pops unless you pick cakesicles.</p><div class="whf-options">${[['Round', 'Round pops', 'Classic, on a stick'], ['Cakesicle', 'Cakesicles', 'Popsicle shape, on a wood stick']]
      .map(([k, t, d]) => `<button type="button" class="whf-option ${S.cakePopShape === k ? 'is-on' : ''}" data-cpshape="${k}" aria-pressed="${S.cakePopShape === k}"><strong>${t}</strong><span>${d}</span></button>`)
      .join('')}</div>`;
    C.cakePopFlavors.forEach((f) => {
      const v = S.cakePops[f] || 0;
      html += stepper(`cp.${f}`, v, { label: f, step: v === 0 || v === 12 ? (v === 0 ? 12 : 6) : 6 });
    });
    html += `</div></details></section>`;

    // Bigfoot Bites
    const bf3 = variant(C.extras, 'Bigfoot Bites, Chocolate (3-Pack)');
    const bf6 = variant(C.extras, 'Bigfoot Bites, Chocolate (6-Pack)');
    if (bf3 || bf6) {
      const bfn = bigfootTotal();
      html += `<section class="whf-card"><details class="whf-more"${bfn ? ' open' : ''}><summary><span class="whf-h3">Bigfoot Bites</span>${bfn ? ` <span class="whf-count is-done">${bfn} added</span>` : ' <span class="whf-more__hint">optional</span>'}</summary><div class="whf-more__body">
        <p class="whf-help">Salty, sweet, crunchy and chocolate: a thick chocolate or white shell around a peanut butter, caramel, pretzel and Ritz center. By the 3.${bf3 && bf6 ? ` ${money(bf3.price)} / 3 · ${money(bf6.price)} / 6.` : ''} White tops can carry your edible photos and can be tinted to your party colors.</p>
        <p class="whf-help"><strong>Contains peanuts.</strong></p>`;
      BIGFOOT.forEach((coat) => {
        html += stepper(`bf.${coat}`, S.bigfoot[coat], { label: coat === 'White' ? 'White' : 'Chocolate', step: 3 });
      });
      html += `</div></details></section>`;
    }

    // Cookies
    const ckInc = cookieIncluded();
    if (C.cookies.length) {
      const left = ckInc - sum(S.cookies);
      const ckExtra = sum(S.cookieExtra);
      const ckOpen = ckInc > 0 || ckExtra > 0;
      html += `<section class="whf-card"><details class="whf-more"${ckOpen ? ' open' : ''}><summary><span class="whf-h3">Cookies</span>${ckInc ? ` <span class="whf-count ${left === 0 ? 'is-done' : ''}">${sum(S.cookies)} of ${ckInc}</span>` : ckExtra ? ` <span class="whf-count is-done">${ckExtra} added</span>` : ` <span class="whf-more__hint">optional</span>`}</summary><div class="whf-more__body">`;
      if (ckInc) {
        html += `<p class="whf-help">Choose ${ckInc} cookies, 6 per flavor. <span class="whf-count ${left === 0 ? 'is-done' : ''}">${sum(S.cookies)} of ${ckInc}</span></p>
          <button type="button" class="whf-pickforme" data-pick="cookies">"Reset to Joni's cookies"</button>`;
        C.cookies.forEach((c) => {
          const v = S.cookies[c.id] || 0;
          html += stepper(`ck.${c.id}`, v, {
            label: c.title,
            sub: 'included',
            max: hasRoom(cookieRead, cookieKeys(), String(c.id), ckInc) ? v + 6 : v,
          });
        });
      }
      html += `<h4 class="whf-h4">Add ${ckInc ? 'more ' : ''}cookies <span>(boxes of 6)</span></h4>`;
      C.cookies.forEach((c) => {
        html += stepper(`ckx.${c.id}`, S.cookieExtra[c.id] || 0, { label: c.title, sub: `${money(c.price)} / 6` });
      });
      html += `</div></details></section>`;
    }
    return html;
  }

  function viewFlavors(part) {
    const coatPrice = up('Flavored Bark (per half dozen)');
    const candyPrice = up('Candy Topping (per half dozen)');
    const carPrice = up('Caramel Wrap Upgrade (per half dozen)');
    let html = `<section class="whf-card ${S.showErrors && !S.bases.length ? 'has-error' : ''}"><h3 class="whf-h3">Dipped in almond bark</h3><p class="whf-help">All of our dipped treats are hand dipped in almond bark. Vanilla is the default because it takes color best. Black treats are dipped in chocolate. Choose one base or both.</p><div class="whf-chips">
      ${C.chocolates
        .map((c) => {
          const on = S.bases.includes(c);
          const locked = on && c === chocolateBase() && blackChosen();
          return `<button type="button" class="whf-chip ${on ? 'is-on' : ''}" data-choc="${esc(c)}" aria-pressed="${on}" ${locked ? 'disabled' : ''}>${esc(c)}</button>`;
        })
        .join('')}
      </div>
      ${S.bases.length > 1 ? '<p class="whf-help whf-help--small">We\'ll split your treats between both bases. Want a specific split? Tell us in Special requests.</p>' : ''}
      ${blackChosen() && chocolateBase() ? `<p class="whf-help whf-help--small">Because your colors include black, those treats start with ${esc(chocolateBase().toLowerCase())} for the deepest, richest black.</p>` : ''}
      </section>`;

    if (part === 'bases') return html;
    html = '';

    DIPPED.forEach((t) => {
      const n = dippedTotal(t.key);
      if (!n) return;
      const used = sum(S.coatings[t.key]) + (t.key === 'pretzels' ? S.caramelUpgrade : 0);
      const typed = t.key === 'oreos' && OT.length > 0;
      const barkHelp = `Your almond bark base is included. Upgrade any half dozen to a flavored bark${coatPrice ? ` for +${money(coatPrice.price)} / half dozen` : ''}. Flavored barks are made with flavor oils and mix-ins blended right into the bark, so every bite stays fresh.`;
      html += `<section class="whf-card ${typed && S.showErrors && oreoProblems().length ? 'has-error' : ''}"><h3 class="whf-h3">${t.label} <span class="whf-count">${n}</span></h3>`;
      if (typed) {
        const chosen = sum(S.oreoType);
        const free = n - chosen;
        html += `<div class="whf-otypes">
          <h4 class="whf-h4 whf-h4--first">Oreo type <span class="whf-count ${chosen === n ? 'is-done' : ''}">${chosen} of ${n}</span></h4>
          <p class="whf-help whf-help--small">Mix any types in half dozens. Same price for every type.</p>`;
        OT.forEach((ot, i) => {
          const v = S.oreoType[i];
          const spare = OT.reduce((a, _, j) => (j === i ? a : Math.max(a, S.oreoType[j])), 0);
          // + borrows a half dozen from the type with the most, and - hands it back the same way
          const canAdd = free >= 6 || spare > 0;
          const canTake = v >= 6 && (spare > 0 || i > 0);
          html += stepper(`otype.${i}`, v, {
            label: ot.label,
            sub: ot.base ? `Dipped in ${esc(ot.base.toLowerCase())}` : '',
            min: canTake ? 0 : v,
            max: canAdd ? v + 6 : v,
          });
        });
        html += `</div><h4 class="whf-h4">Flavored bark</h4><p class="whf-help whf-help--small">${barkHelp}</p>`;
        OT.forEach((ot, i) => {
          const count = S.oreoType[i];
          if (!count) return;
          const tUsed = sum(S.oreoCoat[i]);
          html += `<div class="whf-otype"><p class="whf-otype__head">${esc(ot.label)} <span>${count}</span></p>`;
          // a flavor that doesn't pair only shows up if it somehow has a count, so it can be taken off
          const stray = Object.keys(S.oreoCoat[i]).filter((c) => S.oreoCoat[i][c] && !ot.flavors.includes(c));
          const keys = oreoCoatKeys(i);
          [...ot.flavors, ...stray].forEach((c) => {
            const v = S.oreoCoat[i][c] || 0;
            const room = ot.flavors.includes(c) && hasRoom(oreoCoatRead(i), keys, c, count);
            html += stepper(`ocoat.${i}.${c}`, v, { label: c, max: room ? v + 6 : v });
          });
          const note = pairNote(ot);
          if (note) html += `<p class="whf-pair">${esc(note)}</p>`;
          html += `</div>`;
        });
      } else {
        html += `<p class="whf-help">${barkHelp}</p>`;
        const keys = coatKeys(t.key);
        C.coatings.forEach((c) => {
          const v = S.coatings[t.key][c] || 0;
          html += stepper(`coat.${t.key}.${c}`, v, { label: c, max: hasRoom(coatRead(t.key), keys, c, n) ? v + 6 : v });
        });
      }
      if (t.key === 'pretzels')
        html += stepper('caramelUp', S.caramelUpgrade, {
          label: 'Caramel-wrapped',
          sub: carPrice ? `+${money(carPrice.price)} / half dozen` : '',
          max: hasRoom(coatRead('pretzels'), coatKeys('pretzels'), CARAMEL, n) ? S.caramelUpgrade + 6 : S.caramelUpgrade,
        });
      html += `<div class="whf-classic">Plain almond bark: <strong>${n - used}</strong></div>`;
      html += stepper(`candy.${t.key}`, S.candyCount[t.key], { label: 'Candy topping', sub: candyPrice ? `+${money(candyPrice.price)} / half dozen` : '', max: n });
      html += `</section>`;
    });

    if (sum(S.candyCount) > 0) {
      const needsPick = S.showErrors && !S.candyChoices.length && !S.candyOther.trim();
      html += `<section class="whf-card ${needsPick ? 'has-error' : ''}"><h3 class="whf-h3">Which candy?</h3>
        <p class="whf-help">Pick one or more. Sprinkles are always free. To keep every bite fresh, crunchy toppings are mixed right into the almond bark.</p>
        <div class="whf-chips">
        ${C.candies.map((c) => `<button type="button" class="whf-chip ${S.candyChoices.includes(c) ? 'is-on' : ''}" data-candy="${esc(c)}" aria-pressed="${S.candyChoices.includes(c)}">${esc(c)}</button>`).join('')}
        </div>
        <label class="whf-field"><span>Something else? Type it in</span><input type="text" data-field="candyOther" value="${esc(S.candyOther)}" placeholder="e.g. crushed Butterfinger" maxlength="80"></label>
        <p class="whf-help whf-help--small">Special candy requests are confirmed by us before your order is finalized. We only use candy-coated treats or toppings we can seal in the bark, so nothing goes stale.</p>
      </section>`;
    }
    return html;
  }

  function viewFinishing() {
    const ec = up('Extra Color (over 4)');
    const colorCount = S.colors.length + (S.customColor.trim() ? 1 : 0);
    const decorOpts = [
      { key: 'simple', name: 'Simple', desc: 'Drizzle & sprinkles', v: null },
      { key: 'detailed', name: 'Detailed', desc: 'Piped designs, letters, bows', v: up('Detailed Decorating (per half dozen)') },
      { key: 'showstopper', name: 'Showstopper', desc: 'Molded pieces, characters, toppers', v: up('Showstopper Decorating (per half dozen)') },
    ];
    const bag = up('Individual Bag (per half dozen)');
    const rib = up('Ribbon Tie (per half dozen)');
    const first = up('Edible Images (First Sheet)');
    const more = up('Edible Images (Additional Sheet)');
    const eligible = imageEligible();
    const sticks = stickTreats();
    // the palette Joni reaches for first: white, the blues, and what sits well beside them
    const FEATURED = ['White', 'Baby Blue', 'Royal Blue', 'Navy', 'Teal', 'Silver', 'Lavender', 'Blush Pink'];
    const featuredColors = C.colors.filter((c) => FEATURED.includes(c.name) || S.colors.includes(c.name));
    const restColors = C.colors.filter((c) => !featuredColors.includes(c));
    const restChosen = restColors.filter((c) => S.colors.includes(c.name)).length;


    return `
      <section class="whf-card"><h3 class="whf-h3">Occasion &amp; theme</h3>
        <label class="whf-field"><span>What are we celebrating?</span><input type="text" data-field="occasion" value="${esc(S.occasion)}" placeholder="e.g. Emma's 5th birthday, dinosaur theme" maxlength="140"></label>
        <label class="whf-field"><span>Names, initials, or numbers to include (optional)</span><input type="text" data-field="personalization" value="${esc(S.personalization)}" placeholder="e.g. EMMA, 5" maxlength="80"></label>
      </section>

      <section class="whf-card"><h3 class="whf-h3">Colors <span class="whf-count">${colorCount} of ${C.maxColors}</span></h3>
        <p class="whf-help">Up to ${C.freeColors} colors included${ec && C.maxColors > C.freeColors ? `. Each color after that is +${money(ec.price)}` : ''}. We recommend 2 to 4 for a cohesive look.</p>
        <div class="whf-swatches">
          ${featuredColors
            .map((c) => {
              const on = S.colors.includes(c.name);
              const dis = !on && colorCount >= C.maxColors;
              return `<button type="button" class="whf-swatch ${on ? 'is-on' : ''}" data-color="${esc(c.name)}" ${dis ? 'disabled' : ''} aria-pressed="${on}"><i style="background:${esc(c.hex)}"></i>${esc(c.name)}</button>`;
            })
            .join('')}
        </div>
        ${restColors.length
          ? `<details class="whf-more whf-more--colors"${restChosen ? ' open' : ''}><summary><span>More colors</span>${restChosen ? ` <span class="whf-count is-done">${restChosen} chosen</span>` : ` <span class="whf-more__hint">${restColors.length} more</span>`}</summary><div class="whf-more__body"><div class="whf-swatches">
              ${restColors
                .map((c) => {
                  const on = S.colors.includes(c.name);
                  const dis = !on && colorCount >= C.maxColors;
                  return `<button type="button" class="whf-swatch ${on ? 'is-on' : ''}" data-color="${esc(c.name)}" ${dis ? 'disabled' : ''} aria-pressed="${on}"><i style="background:${esc(c.hex)}"></i>${esc(c.name)}</button>`;
                })
                .join('')}
            </div></div></details>`
          : ''}
        ${blackChosen() && chocolateBase() ? `<p class="whf-note">Black is made on ${esc(chocolateBase().toLowerCase())} for the deepest color, so we've added it to your base.</p>` : ''}
        <label class="whf-field"><span>Need an exact match? Describe it</span><input type="text" data-field="customColor" value="${esc(S.customColor)}" placeholder="e.g. dusty rose to match invitations" maxlength="80" ${!S.customColor && colorCount >= C.maxColors ? 'disabled' : ''}></label>
      </section>

      <section class="whf-card"><h3 class="whf-h3">Decorating</h3>
        <div class="whf-options">
          ${decorOpts
            .map(
              (o) => `<button type="button" class="whf-option ${S.decor === o.key ? 'is-on' : ''}" data-decor="${o.key}">
                <strong>${o.name}</strong><span>${o.desc}</span><em>${o.v ? `+${money(o.v.price)} / half dozen` : 'Included'}</em></button>`
            )
            .join('')}
        </div>
        ${S.decor === 'showstopper' ? '<p class="whf-help">Showstopper designs are confirmed after we review your inspiration photos.</p>' : ''}
      </section>

      ${
        sticks
          ? `<section class="whf-card"><h3 class="whf-h3">Packaging</h3>
        <label class="whf-toggle"><input type="checkbox" data-check="bags" ${S.bags ? 'checked' : ''}><span><strong>Individually bagged</strong> ${bag ? `+${money(bag.price)} / half dozen` : ''}<small>Great for party favors. Cookies are always individually wrapped.</small></span></label>
        <label class="whf-toggle"><input type="checkbox" data-check="ribbon" ${S.ribbon ? 'checked' : ''}><span><strong>Ribbon ties</strong> ${rib ? `+${money(rib.price)} / half dozen` : ''}<small>Tied in your colors on each treat.</small></span></label>
      </section>`
          : ''
      }

      ${
        eligible
          ? `<section class="whf-card"><h3 class="whf-h3">Edible photo toppers</h3>
        <p class="whf-help">Print a photo or logo on Oreos, Rice Krispie treats, or cake pops. ${first ? `${money(first.price)} for the first sheet (up to 20 treats, ${C.maxPhotos} photos)` : ''}${more ? `, ${money(more.price)} each additional sheet` : ''}. Most customers top a third to half of their treats.</p>
        ${stepper('images', S.images, { label: 'Treats with a photo', max: Math.floor(eligible / 6) * 6 })}
        ${S.images === 0 ? `<button type="button" class="whf-link" data-suggest-images>Use our suggestion (${Math.max(6, Math.round(eligible / 2 / 6) * 6)})</button>` : ''}
        ${
          S.images > 0
            ? `<div class="whf-edible-upload ${S.showErrors && !S.edibleFiles.length && !S.customImages ? 'has-error' : ''}">
          <p class="whf-stepper__label">Upload your edible photos <span class="whf-required">Required</span></p>
          <p class="whf-help whf-help--small">Photos are due when you order so we have time to print and ship them. <strong>Edible photos are final once submitted</strong> and can't be changed or refunded after we send them to print. ${C.maxPhotos} different photos per sheet are included, then +${money((up('Extra Edible Photo') || { price: 0 }).price)} each.</p>
          <label class="whf-upload">
            <input type="file" accept="image/*" multiple data-edible-files>
            <span>${S.edibleFiles.length ? `${S.edibleFiles.length} photo${S.edibleFiles.length > 1 ? 's' : ''} uploaded. Tap to change.` : 'Tap to upload edible photos'}</span>
          </label>
          ${S.edibleFiles.length ? `<div class="whf-thumbs">${S.edibleFiles.map((f) => `<img src="${URL.createObjectURL(f)}" alt="">`).join('')}</div>` : ''}
        </div>`
            : ''
        }
        ${S.images > 0 ? stepper('customImages', S.customImages, { label: 'Custom-designed images', step: 1, max: 10, sub: `I design it for you · +${money((up('Custom Designed Image') || { price: 0 }).price)} each. Add the invitation or a note below so I know the look.` }) : ''}
      </section>`
          : ''
      }

      <section class="whf-card ${S.showErrors && S.customImages > 0 && !S.files.length && !S.occasion.trim() && !S.personalization.trim() && !S.notes.trim() ? 'has-error' : ''}"><h3 class="whf-h3">Inspiration${S.customImages > 0 ? ' <span class="whf-count">needed</span>' : ''}</h3>
        <p class="whf-help">${S.customImages > 0 ? "Since I am designing your image, load the invitation or anything that shows the look you are after. " : ""}Share party invites, color palettes, or treats you love. Up to 3 photos.</p>
        <label class="whf-upload">
          <input type="file" accept="image/*" multiple data-files>
          <span>${S.files.length ? `${S.files.length} photo${S.files.length > 1 ? 's' : ''} added. Tap to change.` : 'Tap to add photos'}</span>
        </label>
        ${S.files.length ? `<div class="whf-thumbs">${S.files.map((f) => `<img src="${URL.createObjectURL(f)}" alt="">`).join('')}</div>` : ''}
      </section>`;
  }

  function viewMakeItYours() {
    const upgrades = viewFlavors('details');
    const chosenUpgrades = sum(S.coatings.oreos) + sum(S.coatings.pretzels) + sum(S.coatings.rkt) + S.caramelUpgrade;
    return (
      `<h2 class="whf-h2">Make it yours</h2>
       <p class="whf-help whf-lede">Everything here is set to my usual. Change what you want, skip what you don't.</p>` +
      viewFlavors('bases') +
      viewFinishing() +
      `<section class="whf-card"><details class="whf-more"${chosenUpgrades || (S.showErrors && oreoProblems().length) ? ' open' : ''}>
         <summary><span class="whf-h3">Flavored bark &amp; Oreo types</span>${chosenUpgrades ? ` <span class="whf-count is-done">${chosenUpgrades} upgraded</span>` : ' <span class="whf-more__hint">optional</span>'}</summary>
         <div class="whf-more__body"><p class="whf-help">Your almond bark base is included. Open this to upgrade any half dozen to a flavored bark, or to mix Oreo types.</p>${upgrades}</div>
       </details></section>`
    );
  }

  function viewReview() {
    const ls = lines();
    const t = total();
    const deposit = Math.round((t * C.depositPercent) / 100);
    return `
      <h2 class="whf-h2">Review your box</h2>
      <section class="whf-card whf-review">
        <div class="whf-review__row"><span>Size</span><strong>${esc(S.pkg.name)}</strong></div>
        <div class="whf-review__row"><span>Pickup</span><strong>You choose your day and time at checkout</strong></div>
        ${summaryText()
          .map(([k, v]) => `<div class="whf-review__row"><span>${esc(k)}</span><strong>${esc(v)}</strong></div>`)
          .join('')}
      </section>
      <section class="whf-card">
        ${ls.map((l) => `<div class="whf-review__row"><span>${esc(l.label)}</span><strong>${money(l.cents)}</strong></div>`).join('')}
        <div class="whf-review__row whf-review__total"><span>Total</span><strong>${money(t)}</strong></div>
      </section>
      <section class="whf-card"><h3 class="whf-h3">How would you like to pay?</h3>
        <div class="whf-options">
          <button type="button" class="whf-option ${S.payment === 'deposit' ? 'is-on' : ''}" data-pay="deposit" aria-pressed="${S.payment === 'deposit'}">
            <strong>Pay ${C.depositPercent}% deposit</strong><span>${money(deposit)} today, ${money(t - deposit)} at pickup</span><em>Holds your date</em></button>
          <button type="button" class="whf-option ${S.payment === 'full' ? 'is-on' : ''}" data-pay="full" aria-pressed="${S.payment === 'full'}">
            <strong>Pay in full</strong><span>${money(t)} today, nothing due at pickup</span><em>Easiest pickup</em></button>
        </div>
        <div class="whf-review__row"><span>Due today</span><strong>${money(S.payment === 'full' ? t : deposit)}</strong></div>
        <div class="whf-review__row"><span>Due at pickup</span><strong>${money(S.payment === 'full' ? 0 : t - deposit)}</strong></div>
        ${S.payment === 'deposit' ? `<p class="whf-help whf-help--small">Only the deposit is charged today. The balance is due when you pick up, and we take it then. Checkout shows a far off due date as a placeholder, and your card is never charged automatically.</p>` : ''}
      </section>
      <section class="whf-card"><h3 class="whf-h3">Special requests</h3>
        <label class="whf-field"><span>Allergies, favorite characters, pickup questions, or anything else we should know</span><textarea data-field="notes" rows="4" maxlength="1000" placeholder="Optional">${esc(S.notes)}</textarea></label>
        <p class="whf-help whf-help--small">We'll reach out if a request changes your price or needs a quick chat.</p>
      </section>
      <section class="whf-card whf-policy ${S.showErrors && !S.agreed ? 'has-error' : ''}">${policyHtml}
        <label class="whf-toggle"><input type="checkbox" data-check="agreed" ${S.agreed ? 'checked' : ''}><span><strong>I've read and agree to the order policy</strong></span></label>
      </section>`;
  }

  function summaryText() {
    const rows = [];
    DIPPED.forEach((t) => {
      const n = dippedTotal(t.key);
      if (!n) return;
      const parts = [];
      const used = sum(S.coatings[t.key]) + (t.key === 'pretzels' ? S.caramelUpgrade : 0);
      if (n - used) parts.push(`${n - used} plain`);
      Object.entries(S.coatings[t.key]).forEach(([c, v]) => v && parts.push(`${v} ${c}`));
      if (t.key === 'pretzels' && S.caramelUpgrade) parts.push(`${S.caramelUpgrade} caramel-wrapped`);
      if (S.candyCount[t.key]) parts.push(`${S.candyCount[t.key]} candy-topped`);
      rows.push([`${t.label} (${n})`, parts.join(', ')]);
      if (t.key === 'oreos' && OT.length) {
        const types = OT.map((ot, i) => ({ ot, i, count: S.oreoType[i] })).filter((x) => x.count);
        if (types.length)
          rows.push(['Oreo types', types.map(({ ot, count }) => `${count} ${ot.name}${ot.base ? ` (${ot.base.toLowerCase()})` : ''}`).join(', ')]);
        if (sum(S.coatings.oreos))
          rows.push([
            'Oreo flavors',
            types
              .map(({ ot, i, count }) => {
                const bits = Object.entries(S.oreoCoat[i]).filter(([, v]) => v).map(([c, v]) => `${v} ${c}`);
                const plain = count - sum(S.oreoCoat[i]);
                if (plain > 0) bits.push(`${plain} plain`);
                return `${ot.name}: ${bits.join(', ')}`;
              })
              .join('; '),
          ]);
      }
    });
    if (S.extra.caramel) rows.push(['Caramel-Wrapped Pretzels', String(S.extra.caramel)]);
    if (bigfootTotal())
      rows.push([`Bigfoot Bites (${bigfootTotal()})`, BIGFOOT.filter((c) => S.bigfoot[c]).map((c) => `${S.bigfoot[c]} ${c.toLowerCase()}`).join(', ') + ' (contains peanuts)']);
    const cp = Object.entries(S.cakePops).filter(([, v]) => v);
    if (cp.length) rows.push([`Cake pops (${cakePopTotal()})`, `${S.cakePopShape === 'Cakesicle' ? 'Cakesicles' : 'Round pops'}: ` + cp.map(([f, v]) => `${v} ${f}`).join(', ')]);
    const ck = C.cookies
      .map((c) => [c.title, (S.cookies[c.id] || 0) + (S.cookieExtra[c.id] || 0)])
      .filter(([, v]) => v);
    if (ck.length) rows.push([`Cookies (${ck.reduce((a, [, v]) => a + v, 0)})`, ck.map(([f, v]) => `${v} ${f}`).join(', ')]);
    const fixedBases = dippedTotal('oreos')
      ? OT.filter((ot, i) => S.oreoType[i] && ot.base).map((ot) => `, ${ot.label} on ${ot.base.toLowerCase()}`).join('')
      : '';
    if (S.bases.length) rows.push(['Dipped in', S.bases.join(' and ') + (S.bases.length > 1 ? ' (split)' : '') + (blackChosen() ? ', black treats on chocolate' : '') + fixedBases]);
    const candy = [...S.candyChoices, S.candyOther.trim() && `Other: ${S.candyOther.trim()}`].filter(Boolean);
    if (sum(S.candyCount) && candy.length) rows.push(['Candy', candy.join(', ')]);
    const colors = [...S.colors, S.customColor.trim()].filter(Boolean);
    if (colors.length) rows.push(['Colors', colors.join(', ')]);
    rows.push(['Decorating', { simple: 'Simple', detailed: 'Detailed', showstopper: 'Showstopper' }[S.decor]]);
    const pack = [S.bags && 'Individually bagged', S.ribbon && 'Ribbon ties'].filter(Boolean);
    if (pack.length) rows.push(['Packaging', pack.join(', ')]);
    if (S.occasion.trim()) rows.push(['Occasion', S.occasion.trim()]);
    if (S.personalization.trim()) rows.push(['Personalization', S.personalization.trim()]);
    if (S.images)
      rows.push([
        'Edible photos',
        `${S.images} treats${S.edibleFiles.length ? `, ${S.edibleFiles.length} photo${S.edibleFiles.length > 1 ? 's' : ''} uploaded` : ''}${S.customImages ? `, ${S.customImages} custom-designed` : ''}`,
      ]);
    if (S.notes.trim()) rows.push(['Special requests', S.notes.trim()]);
    return rows;
  }

  function render() {
    normalize();
    const choc = chocolateBase();
    if (blackChosen() && choc && !S.bases.includes(choc)) S.bases = C.chocolates.filter((c) => c === choc || S.bases.includes(c));
    const views = [viewSize, viewTreats, viewMakeItYours, viewReview];
    const t = total();
    const probs = problems(S.step);
    const errorBox =
      S.showErrors && probs.length
        ? `<div class="whf-errors" role="alert" id="whf-errors"><strong>Almost there! Please finish these first:</strong><ul>${probs.map((m) => `<li>${esc(m)}</li>`).join('')}</ul></div>`
        : '';
    const primary =
      S.step < STEPS.length - 1
        ? `<button type="button" class="whf-btn" data-next>Continue to ${STEPS[S.step + 1]}</button>`
        : `<button type="button" class="whf-btn" data-submit ${S.busy ? 'disabled' : ''}>${S.busy ? 'Adding to cart...' : 'Reserve my date'}</button>`;
    const back = S.step > 0 ? '<button type="button" class="whf-btn whf-btn--ghost" data-back>Back</button>' : '';
    const focusKey = document.activeElement && document.activeElement.dataset ? document.activeElement.dataset.field : null;
    const scrollY = window.scrollY;

    app.innerHTML = `
      <ol class="whf-progress" aria-label="Steps">
        ${STEPS.map(
          (s, i) =>
            `<li class="${i === S.step ? 'is-current' : ''} ${i < S.step ? 'is-done' : ''}"><button type="button" data-goto="${i}"><span>${i + 1}</span>${s}</button></li>`
        ).join('')}
      </ol>
      <div class="whf-layout">
        <div class="whf-main">
          ${S.pkg || S.step === 0 ? views[S.step]() : viewSize()}
          ${S.error ? `<div class="whf-errors" role="alert">${esc(S.error)}</div>` : errorBox}
          ${S.step > 0 ? `<div class="whf-stepnav">${back}${primary}</div>` : ''}
        </div>
        <aside class="whf-summary" aria-live="polite">
          ${
            S.pkg
              ? `<div class="whf-summary__pkg">${S.pkg.image ? `<img src="${S.pkg.image}" alt="">` : ''}<div><strong>${esc(S.pkg.name)}</strong><span>Pickup chosen at checkout</span></div></div>
                 <div class="whf-summary__lines">${lines().map((l) => `<div><span>${esc(l.label)}</span><span>${money(l.cents)}</span></div>`).join('')}</div>`
              : '<p class="whf-help">Choose a size to start.</p>'
          }
          <div class="whf-summary__total"><span>Total</span><strong>${money(t)}</strong></div>
          ${S.pkg ? `<div class="whf-summary__deposit">${S.payment === 'full' ? 'Paying in full today' : `${money(Math.round((t * C.depositPercent) / 100))} deposit today`}</div>` : ''}
        </aside>
      </div>
      <div class="whf-bar">
        <div class="whf-bar__inner whf-wrap">
          <div class="whf-bar__total"><span>${S.pkg ? 'Total' : ''}</span><strong>${S.pkg ? money(t) : ''}</strong></div>
          ${S.showErrors && probs.length ? `<p class="whf-error">${esc(probs[0])}</p>` : ''}
          <div class="whf-bar__buttons">${S.step > 0 ? back + primary : ''}</div>
        </div>
      </div>`;

    if (focusKey) {
      const el = app.querySelector(`[data-field="${focusKey}"]`);
      if (el) {
        el.focus();
        const len = el.value.length;
        try { el.setSelectionRange(len, len); } catch (e) {}
      }
    }
    window.scrollTo(0, scrollY);
  }

  function go(step) {
    S.step = step;
    S.error = '';
    S.showErrors = false;
    render();
    root.querySelector('.whf-progress').scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // move forward only when everything required so far is filled in; otherwise explain what's missing
  function tryGo(step) {
    if (step <= S.step) return go(step);
    for (let s = S.step; s < step; s++) {
      if (problems(s).length) {
        if (s !== S.step) S.step = s;
        S.showErrors = true;
        render();
        const box = document.getElementById('whf-errors');
        if (box) box.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
    }
    go(step);
  }

  /* ---------- events ---------- */
  /* spread a total across keys in half dozens, biggest share first */
  function spread(total, keys) {
    const out = {};
    keys.forEach((k) => (out[k] = 0));
    if (!keys.length) return out;
    const halves = Math.round(total / 6);
    for (let i = 0; i < halves; i++) out[keys[i % keys.length]] += 6;
    return out;
  }

  /* fill the box with Joni's default mix so nobody starts from zero */
  function prefillPicks() {
    if (!S.pkg) return;
    const fixed = DIPPED.reduce((a, t) => a + (S.pkg[t.key] || 0), 0);
    if (S.pkg.mixDipped > 0) {
      S.mix = spread(S.pkg.mixDipped, ['oreos', 'pretzels', 'rkt']);
    } else if (fixed > 0) {
      // turn a fixed box into an adjustable one, starting at the counts Joni set
      S.pkg.mixDipped = fixed;
      S.mix = { oreos: S.pkg.oreos || 0, pretzels: S.pkg.pretzels || 0, rkt: S.pkg.rkt || 0 };
      S.pkg.defaultMix = { oreos: S.pkg.oreos || 0, pretzels: S.pkg.pretzels || 0, rkt: S.pkg.rkt || 0 };
      DIPPED.forEach((t) => (S.pkg[t.key] = 0));
    }
    // start every Oreo as the first type; the chooser is still there to change it
    if (OT.length) {
      S.oreoType = OT.map(() => 0);
      S.oreoType[0] = (S.mix.oreos || 0) + (S.extra.oreos || 0);
      S.oreoCoat = OT.map(() => ({}));
    }
    const cpInc = cakePopIncluded();
    S.cakePops = {};
    if (cpInc > 0) {
      const flavors = C.cakePopFlavors.slice(0, Math.max(1, Math.round(cpInc / 12)));
      flavors.forEach((f, i) => (S.cakePops[f] = i === flavors.length - 1 ? cpInc - 12 * (flavors.length - 1) : 12));
    }
    const ckInc = cookieIncluded();
    S.cookies = {};
    if (ckInc > 0 && C.cookies.length) {
      const faves = ['Legendary Squatch', 'Monster Mash', 'Jurassic Golden Funfetti', 'Scarlet Raptor Cookies & Cream'];
      const ordered = C.cookies.slice().sort((x, y) => {
        const ix = faves.indexOf(x.title), iy = faves.indexOf(y.title);
        return (ix === -1 ? 99 : ix) - (iy === -1 ? 99 : iy);
      });
      const picked = spread(ckInc, ordered.map((c) => String(c.id)));
      Object.entries(picked).forEach(([k, v]) => { if (v) S.cookies[k] = v; });
    }
  }

  // box photo galleries on the size step
  app.addEventListener(
    'scroll',
    (e) => {
      const t = e.target;
      if (!t.classList || !t.classList.contains('whf-gal__track')) return;
      const c = t.parentElement.querySelector('.whf-gal__count');
      if (c) c.textContent = `${Math.round(t.scrollLeft / t.clientWidth) + 1} / ${t.children.length}`;
    },
    true
  );

  app.addEventListener('click', (e) => {
    const nav = e.target.closest('[data-galnav]');
    if (nav) {
      const t = nav.parentElement.querySelector('.whf-gal__track');
      const dir = Number(nav.dataset.galnav);
      const end = t.scrollLeft + t.clientWidth >= t.scrollWidth - 2;
      if (dir > 0 && end) t.scrollTo({ left: 0, behavior: 'smooth' });
      else if (dir < 0 && t.scrollLeft <= 2) t.scrollTo({ left: t.scrollWidth, behavior: 'smooth' });
      else t.scrollBy({ left: t.clientWidth * dir, behavior: 'smooth' });
      return;
    }
    const photo = e.target.closest('.whf-gal__track');
    if (photo) {
      const wrap = photo.closest('.whf-pkg-wrap');
      const pick = wrap && wrap.querySelector('[data-pkg]');
      if (pick) pick.click();
      return;
    }
    const b = e.target.closest('button');
    if (!b || b.disabled) return;
    const d = b.dataset;

    if (d.pick) {
      if (d.pick === 'mix') {
        S.mix = S.pkg.defaultMix ? { ...S.pkg.defaultMix } : spread(S.pkg.mixDipped, ['oreos', 'pretzels', 'rkt']);
      } else if (d.pick === 'all') {
        prefillPicks();
      } else if (d.pick === 'cookies') {
        // Joni's most requested flavors first, then whatever else is available
        const faves = ['Legendary Squatch', 'Monster Mash', 'Jurassic Golden Funfetti', 'Scarlet Raptor Cookies & Cream'];
        const ordered = C.cookies.slice().sort((x, y) => {
          const ix = faves.indexOf(x.title), iy = faves.indexOf(y.title);
          return (ix === -1 ? 99 : ix) - (iy === -1 ? 99 : iy);
        });
        const picked = spread(cookieIncluded(), ordered.map((c) => String(c.id)));
        S.cookies = {};
        Object.entries(picked).forEach(([k, v]) => { if (v) S.cookies[k] = v; });
      } else if (d.pick === 'cakepops') {
        const inc = cakePopIncluded();
        const flavors = C.cakePopFlavors.slice(0, Math.max(1, Math.round(inc / 12)));
        S.cakePops = {};
        flavors.forEach((f, i) => (S.cakePops[f] = i === flavors.length - 1 ? inc - 12 * (flavors.length - 1) : 12));
      }
      render();
      return;
    }

    if (d.pkg) {
      const next = C.packages.find((p) => p.handle === d.pkg);
      if (!S.pkg || S.pkg.handle !== next.handle) {
        S.pkg = next;
        S.mix = { oreos: 0, pretzels: 0, rkt: 0 };
        S.cookies = {};
        S.cakePops = {};
        prefillPicks();
      }
      return go(1);
    }
    if (d.month) {
      S.calMonth = new Date(S.calMonth.getFullYear(), S.calMonth.getMonth() + Number(d.month), 1);
      return render();
    }
    if (d.date) {
      S.date = d.date;
      return render();
    }
    if (d.goto) return tryGo(Number(d.goto));
    if ('back' in d) return go(S.step - 1);
    if ('next' in d) return tryGo(S.step + 1);
    if ('submit' in d) {
      if (problems(5).length) {
        S.showErrors = true;
        render();
        const box = document.getElementById('whf-errors');
        if (box) box.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      return submit();
    }
    if (d.choc) {
      S.bases = S.bases.includes(d.choc) ? S.bases.filter((c) => c !== d.choc) : [...S.bases, d.choc];
      S.bases = C.chocolates.filter((c) => S.bases.includes(c)); // keep list order
      render();
    }
    if (d.candy) {
      S.candyChoices = S.candyChoices.includes(d.candy) ? S.candyChoices.filter((c) => c !== d.candy) : [...S.candyChoices, d.candy];
      render();
    }
    if (d.color) {
      S.colors = S.colors.includes(d.color) ? S.colors.filter((c) => c !== d.color) : [...S.colors, d.color];
      // black is built on chocolate almond bark
      const choc = chocolateBase();
      if (blackChosen() && choc && !S.bases.includes(choc)) {
        S.bases = C.chocolates.filter((c) => c === choc || S.bases.includes(c));
        S.baseNotice = true;
      }
      render();
    }
    if (d.decor) (S.decor = d.decor), render();
    if (d.cpshape) (S.cakePopShape = d.cpshape), render();
    if (d.pay) (S.payment = d.pay), render();
    if ('suggestImages' in d) {
      S.images = Math.max(6, Math.round(imageEligible() / 2 / 6) * 6);
      render();
    }
    if (d.step) {
      applyStep(d.step, Number(d.delta));
      render();
    }
  });

  function applyStep(name, delta) {
    const [kind, a, ...rest] = name.split('.');
    const b = rest.join('.');
    const clamp = (v) => Math.max(0, v);
    if (kind === 'mix') moveInGroup(mixRead, mixWrite, mixKeys, a, delta, S.pkg ? S.pkg.mixDipped : 0);
    else if (kind === 'extra') S.extra[a] = clamp(S.extra[a] + delta);
    else if (kind === 'bf') S.bigfoot[a] = clamp(S.bigfoot[a] + delta);
    else if (kind === 'cp') {
      const cur = S.cakePops[a] || 0;
      // new flavors start at a dozen; dropping below a dozen removes the flavor
      let v = cur + delta;
      if (cur === 0 && delta > 0) v = 12;
      if (cur === 12 && delta < 0) v = 0;
      S.cakePops[a] = clamp(v);
    } else if (kind === 'ck') moveInGroup(cookieRead, cookieWrite, cookieKeys(), a, delta, cookieIncluded());
    else if (kind === 'ckx') S.cookieExtra[a] = clamp((S.cookieExtra[a] || 0) + delta);
    else if (kind === 'coat') moveInGroup(coatRead(a), coatWrite(a), coatKeys(a), b, delta, dippedTotal(a));
    else if (kind === 'otype') moveInGroup(typeRead, typeWrite, typeKeys(), Number(a), delta, dippedTotal('oreos'), false);
    else if (kind === 'ocoat') {
      const i = Number(a);
      moveInGroup(oreoCoatRead(i), oreoCoatWrite(i), oreoCoatKeys(i), b, delta, S.oreoType[i]);
    } else if (kind === 'caramelUp')
      moveInGroup(coatRead('pretzels'), coatWrite('pretzels'), coatKeys('pretzels'), CARAMEL, delta, dippedTotal('pretzels'));
    else if (kind === 'candy') S.candyCount[a] = clamp(S.candyCount[a] + delta);
    else if (kind === 'images') S.images = clamp(S.images + delta);
    else if (kind === 'customImages') S.customImages = clamp(S.customImages + delta);
    normalize();
  }

  // keep upgrades within the number of treats after counts change
  function normalize() {
    if (OT.length) {
      const n = dippedTotal('oreos');
      // new Oreos start as the first type; fewer Oreos come off the first type, then the last
      if (n > S.oreoSeen) S.oreoType[0] += n - S.oreoSeen;
      S.oreoSeen = n;
      let over = sum(S.oreoType) - n;
      [0, ...OT.map((_, i) => i).slice(1).reverse()].forEach((i) => {
        if (over <= 0) return;
        const take = Math.min(over, S.oreoType[i]);
        S.oreoType[i] -= take;
        over -= take;
      });
      const all = {};
      OT.forEach((ot, i) => {
        let used = 0;
        if (!S.oreoType[i]) S.oreoCoat[i] = {}; // no Oreos of this type left, so its flavors go too
        Object.keys(S.oreoCoat[i]).forEach((c) => {
          const v = Math.max(0, Math.min(S.oreoCoat[i][c], Math.floor((S.oreoType[i] - used) / 6) * 6));
          S.oreoCoat[i][c] = v;
          used += v;
          if (v) all[c] = (all[c] || 0) + v;
        });
      });
      S.coatings.oreos = all;
    }
    DIPPED.forEach((t) => {
      const n = dippedTotal(t.key);
      let used = 0;
      if (t.key === 'oreos' && OT.length) used = n; // already balanced per type above
      else Object.keys(S.coatings[t.key]).forEach((c) => {
        const v = Math.min(S.coatings[t.key][c], Math.floor((n - used) / 6) * 6);
        S.coatings[t.key][c] = Math.max(0, v);
        used += S.coatings[t.key][c];
      });
      if (t.key === 'pretzels') S.caramelUpgrade = Math.max(0, Math.min(S.caramelUpgrade, n - used));
      S.candyCount[t.key] = Math.min(S.candyCount[t.key], n);
    });
    const eligible = Math.floor(imageEligible() / 6) * 6;
    if (S.images > eligible) S.images = eligible;
  }

  app.addEventListener('input', (e) => {
    const f = e.target.dataset.field;
    if (f) {
      S[f] = e.target.value;
      if (f === 'customColor' || f === 'occasion' || f === 'personalization' || f === 'candyOther') {
        clearTimeout(app._t);
        app._t = setTimeout(render, f === 'customColor' ? 250 : 600);
      }
    }
  });
  app.addEventListener('change', (e) => {
    const c = e.target.dataset.check;
    if (c) {
      S[c] = e.target.checked;
      render();
    }
    const images = (list, max) =>
      Array.from(list || [])
        .filter((f) => f.type.startsWith('image/') && f.size <= 20 * 1024 * 1024)
        .slice(0, max);
    if ('files' in e.target.dataset) {
      S.files = images(e.target.files, C.maxPhotos);
      render();
    }
    if ('edibleFiles' in e.target.dataset) {
      S.edibleFiles = images(e.target.files, 10);
      render();
    }
  });

  /* ---------- add to cart ---------- */
  async function submit() {
    if (problems(5).length) return;
    S.busy = true;
    S.error = '';
    render();
    const ref = 'WHF-' + Date.now().toString(36).toUpperCase();
    const ls = lines();
    const items = [];
    ls.forEach((l) =>
      (l.cart || []).forEach(({ v, q }) => {
        if (q <= 0) return;
        const item = { id: v.id, quantity: q, properties: { 'For box': S.pkg.name, _box_ref: ref } };
        if (S.payment === 'deposit' && v.sellingPlan) item.selling_plan = v.sellingPlan;
        items.push(item);
      })
    );
    // included cookies ride along at $0 via the package; they are listed on the package line

    try {
      if (items.length) {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          throw new Error(j.description || 'Something went wrong adding your extras.');
        }
      }

      const form = document.createElement('form');
      form.method = 'post';
      form.action = '/cart/add';
      form.enctype = 'multipart/form-data';
      form.hidden = true;
      const add = (name, value) => {
        const i = document.createElement('input');
        i.type = 'hidden';
        i.name = name;
        i.value = value;
        form.appendChild(i);
      };
      add('id', S.pkg.variantId);
      add('quantity', 1);
      if (S.payment === 'deposit' && S.pkg.sellingPlan) add('selling_plan', S.pkg.sellingPlan);
      add('properties[Payment]', S.payment === 'full' ? 'Paid in full' : `${C.depositPercent}% deposit, balance at pickup`);
      summaryText().forEach(([k, v]) => add(`properties[${k}]`, v));
      add('properties[_box_ref]', ref);
      add('properties[_capacity_points]', S.pkg.points);
      add('return_to', '/cart');
      const attach = (files, label) =>
        files.forEach((f, i) => {
          const input = document.createElement('input');
          input.type = 'file';
          input.name = `properties[${label} ${i + 1}]`;
          const one = new DataTransfer();
          one.items.add(f);
          input.files = one.files;
          form.appendChild(input);
        });
      if (S.images > 0) attach(S.edibleFiles, 'Edible photo');
      attach(S.files, 'Inspiration photo');
      document.body.appendChild(form);
      form.submit();
    } catch (err) {
      S.busy = false;
      S.error = err.message;
      render();
    }
  }

  render();
})();
